import {api} from "../api";

import {FormUtil} from "../forms/formUtil";
import {Combobox} from "./combobox";
import {mergeProperties} from "@/shared/utils/misc";

export interface DescriptionTypeaheadConfig {
	element: JQuery;
	url: string;
	maxQueryLength?: number; // 0 for not query limit
	ignoreMultiline?: boolean;
}

export class DescriptionTypeahead {

	public static EVENT_CHANGED = "changed";

	private descriptionElement: JQuery;
	private filter = {};
	private needsReset = false;

	constructor(public config: DescriptionTypeaheadConfig) {
		config.maxQueryLength = config.maxQueryLength != null ? config.maxQueryLength : 30;
		config.ignoreMultiline = config.ignoreMultiline != null ? config.ignoreMultiline : true;
		this.init();
	}

	public setFilter(filter: unknown) {
		this.filter = filter;
		this.setNeedsReset();
	}

	private init() {
		const description = this.config.element.find(".description:not(.tt-hint)");
		this.descriptionElement = description;
		const button = this.config.element.find(".toggleDescriptionButton");
		description.keydown((e) => {
			if (e.which === 9 && !e.shiftKey) { // handle tab key manually to prevent typeahead from selecting the first element
				e.stopImmediatePropagation();
				FormUtil.focusNextInput(description);
				return false;
			}
			// ie: on a up/down keypress, the typeahead-list selection-, but not of the cursor movement-event is performed - even if there are no items in the list
			// when the list is empty, only the standard textarea (cursor) events should be performed
			if ((e.which === 38 || e.which === 40) && description.closest(".twitter-typeahead").find(".tt-menu").hasClass("tt-empty")) {
				e.stopImmediatePropagation();
			}
		});
		let debounceTimeout: number = null;
		description.typeahead({
			minLength: 0
		}, {
			limit: 20, // must be double of api limit because of bug in library
			async: true,
			source: (query, processSync, processAsync) => {
				clearTimeout(debounceTimeout);
				debounceTimeout = setTimeout(() => {
					debounceTimeout = null;
					if (this.shouldProcess(query)) {
						api(this.config.url)
							.get(mergeProperties(this.filter, {query: query}))
							.done((data: { descriptions: string[] }) => {
								return processAsync(data.descriptions);
							});
					}
				}, 350);
			}
		}).on("typeahead:beforeclose", () => {
			return !toggleDescriptionButtonWillClick;
		}).on("typeahead:close", () => {
			button.find("i").switchClass("fa-caret-up", "fa-caret-down");
			description.closest(".twitter-typeahead").find(".tt-hint").hide();
		}).on("typeahead:beforeopen", () => {
			if (this.needsReset) {
				this.reset();
			}
		}).on("typeahead:open", () => {
			const ttHint = description.closest(".twitter-typeahead").find(".tt-hint");
			if (ttHint.is(":hidden")) {
				ttHint.show();
			}
			button.find("i").switchClass("fa-caret-down", "fa-caret-up");
		}).on("typeahead:select", () => {
			this.onChanged();
		});
		description.closest(".twitter-typeahead").find(".tt-hint").hide();
		description.prop("spellcheck", "true");

		let toggleDescriptionButtonWillClick = false;
		button.mousedown(() => {
			toggleDescriptionButtonWillClick = true;
		}).mouseup(() => {
			toggleDescriptionButtonWillClick = false;
		}).mouseleave(() => {
			toggleDescriptionButtonWillClick = false;
		}).click(() => {
			if (description.typeahead("isOpen")) {
				description.typeahead("close");
			} else {
				description.focus();
			}
		});
	}

	private onChanged() {
		$(this).triggerHandler(Combobox.EVENT_CHANGED);
	}

	public setNeedsReset() {
		this.needsReset = true;
		if (this.descriptionElement.typeahead("isOpen")) {
			this.reset();
		}
	}

	private reset() {
		this.needsReset = false;
		const description = this.config.element.find(".description:not(.tt-hint)");
		// typeahead only reloads if query changes and there is no way to reset that. So we have to set some random value and set the description again
		const val = (description.val() as string) || "";
		description.typeahead("val", val + "#");
		description.typeahead("val", val);
	}

	private shouldProcess(query: string): boolean {
		return !query ||
			(!this.config.ignoreMultiline || query.indexOf("\n") === -1) &&
			(this.config.maxQueryLength === 0 || query.length <= this.config.maxQueryLength);
	}
}
